import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Input } from '@/components/ui/input';
import { StepShell } from '@/components/signup/StepShell';
import { SignupCheckEmail } from '@/components/signup/SignupCheckEmail';
import { Eye, EyeOff, Check } from 'lucide-react';

const DRAFT_KEY = 'rc_signup_draft';
const STEPS = ['email', 'password', 'confirm', 'name', 'company', 'phone', 'referral'];
const REFERRALS = [
  ['google', 'Google Search'], ['facebook', 'Facebook'], ['instagram', 'Instagram'],
  ['word_of_mouth', 'Word of Mouth'], ['other', 'Other'],
];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const inputCls = 'h-14 text-lg bg-[#0f1724] border-white/15 text-white placeholder:text-white/30 focus:border-[#C6A14A] focus-visible:ring-[#C6A14A]/30';

export const formatPhone = (v) => {
  const d = v.replace(/\D/g, '').replace(/^1(?=\d{10})/, '').slice(0, 10);
  if (d.length < 4) return d;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

const passwordChecks = (p) => [
  { met: p.length >= 8, text: '8+ characters' },
  { met: /[A-Z]/.test(p), text: 'Uppercase letter' },
  { met: /[a-z]/.test(p), text: 'Lowercase letter' },
  { met: /\d/.test(p), text: 'Number' },
];

const loadDraft = () => {
  try { return JSON.parse(localStorage.getItem(DRAFT_KEY)) || {}; } catch { return {}; }
};

export default function Register() {
  const { register } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const draft = useRef(loadDraft()).current;

  const [form, setForm] = useState({
    email: draft.email || '', fullName: draft.fullName || '', company: draft.company || '',
    phone: draft.phone || '', referralSource: draft.referralSource || '', referralOther: draft.referralOther || '',
  });
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [index, setIndex] = useState(draft.email ? 1 : 0);
  const [direction, setDirection] = useState(1);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const inputRef = useRef(null);

  const returnToParam = searchParams.get('returnTo');
  const packageParam = searchParams.get('package');
  const fromPath = location.state?.from?.pathname;
  let redirectAfter = returnToParam
    ? decodeURIComponent(returnToParam)
    : (typeof fromPath === 'string' && fromPath.startsWith('/')) ? fromPath : '/welcome';
  if (packageParam && !returnToParam) redirectAfter = `/book?type=home&package=${packageParam}`;

  useEffect(() => {
    if (!registeredEmail) localStorage.setItem(DRAFT_KEY, JSON.stringify(form));
  }, [form, registeredEmail]);

  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus({ preventScroll: true }), 320);
    return () => clearTimeout(t);
  }, [index]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));
  const go = (delta) => { setError(''); setDirection(delta); setIndex((i) => Math.min(Math.max(i + delta, 0), STEPS.length - 1)); };
  const checks = passwordChecks(password);

  const submit = async () => {
    setError('');
    setLoading(true);
    const email = form.email.toLowerCase().trim();
    const result = await register(email, password, redirectAfter);
    if (result.success) {
      localStorage.setItem('rc_pending_profile', JSON.stringify({
        full_name: form.fullName.trim() || null, company: form.company.trim() || null,
        phone: form.phone.trim() || null, referral_source: form.referralSource || null,
        referral_other: form.referralSource === 'other' ? form.referralOther.trim() : null,
        redirect_after: redirectAfter,
      }));
      localStorage.removeItem(DRAFT_KEY);
      setRegisteredEmail(email);
    } else {
      setError(result.error || 'Registration failed. Please try again.');
    }
    setLoading(false);
  };

  if (registeredEmail) {
    return <SignupCheckEmail email={registeredEmail} returnToParam={returnToParam} redirectAfter={redirectAfter} />;
  }

  const loginLink = returnToParam ? `/login?returnTo=${encodeURIComponent(returnToParam)}` : '/login';
  const pwToggle = (
    <button type="button" onClick={() => setShowPassword((s) => !s)} aria-label={showPassword ? 'Hide password' : 'Show password'}
      className="absolute right-4 top-1/2 -translate-y-1/2 text-white/50 hover:text-white" data-testid="signup-toggle-password">
      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
    </button>
  );

  const screens = {
    email: {
      title: "What's your email?", hint: "We'll send your booking confirmations and galleries here.",
      valid: EMAIL_RE.test(form.email.trim()),
      footer: <>Already have an account? <Link to={loginLink} state={{ from: { pathname: redirectAfter } }} className="text-[#C6A14A] font-medium" data-testid="signup-login-link">Sign in</Link></>,
      body: <Input ref={inputRef} type="email" inputMode="email" autoComplete="email" placeholder="you@brokerage.com" value={form.email} onChange={set('email')} className={inputCls} data-testid="register-email-input" />,
    },
    password: {
      title: 'Create a password', hint: form.email ? `For ${form.email.trim()}` : null,
      valid: checks.every((c) => c.met),
      body: (
        <>
          <div className="relative">
            <Input ref={inputRef} type={showPassword ? 'text' : 'password'} autoComplete="new-password" placeholder="Create a strong password" value={password} onChange={(e) => setPassword(e.target.value)} className={`${inputCls} pr-12`} data-testid="register-password-input" />
            {pwToggle}
          </div>
          <div className="flex gap-1.5" data-testid="password-strength">
            {checks.map((c, i) => <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${i < checks.filter((x) => x.met).length ? 'bg-[#C6A14A]' : 'bg-white/10'}`} />)}
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            {checks.map((c) => (
              <span key={c.text} className={`flex items-center gap-1.5 text-xs ${c.met ? 'text-emerald-400' : 'text-white/40'}`}>
                <Check className="h-3.5 w-3.5" />{c.text}
              </span>
            ))}
          </div>
        </>
      ),
    },
    confirm: {
      title: 'Confirm your password', hint: 'Type it once more so we know it’s right.',
      valid: confirm.length > 0 && confirm === password,
      body: (
        <>
          <div className="relative">
            <Input ref={inputRef} type={showPassword ? 'text' : 'password'} autoComplete="new-password" placeholder="Re-enter password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className={`${inputCls} pr-12`} data-testid="register-confirm-password-input" />
            {pwToggle}
          </div>
          {confirm.length > 0 && confirm !== password && <p className="text-xs text-red-400" data-testid="password-mismatch">Passwords don&apos;t match yet</p>}
        </>
      ),
    },
    name: {
      title: "What's your name?", hint: 'So we know who we’re shooting for.',
      valid: form.fullName.trim().length > 1,
      body: <Input ref={inputRef} autoComplete="name" autoCapitalize="words" placeholder="Full name" value={form.fullName} onChange={set('fullName')} className={inputCls} data-testid="register-fullname-input" />,
    },
    company: {
      title: 'Which company or brokerage?', hint: 'Optional — helps us brand your media.', optional: true, valid: true,
      body: <Input ref={inputRef} autoComplete="organization" placeholder="e.g. RE/MAX, Keller Williams" value={form.company} onChange={set('company')} className={inputCls} data-testid="register-company-input" />,
    },
    phone: {
      title: 'Best phone number?', hint: 'Optional — for shoot-day updates.', optional: true,
      valid: !form.phone || form.phone.replace(/\D/g, '').length === 10,
      body: <Input ref={inputRef} type="tel" inputMode="tel" autoComplete="tel" placeholder="(928) 555-1234" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: formatPhone(e.target.value) }))} className={inputCls} data-testid="register-phone-input" />,
    },
    referral: {
      title: 'How did you hear about us?', optional: true, continueLabel: 'Create Account',
      valid: form.referralSource !== 'other' || form.referralOther.trim().length > 0,
      body: (
        <>
          <div className="flex flex-wrap gap-2" data-testid="register-referral-chips">
            {REFERRALS.map(([value, label]) => (
              <button key={value} type="button" onClick={() => setForm((f) => ({ ...f, referralSource: f.referralSource === value ? '' : value }))}
                className={`px-4 py-2.5 rounded-full border text-sm transition-colors ${form.referralSource === value ? 'bg-[#C6A14A] border-[#C6A14A] text-[#050816] font-medium' : 'border-white/15 text-[#E8E4DE] hover:border-[#C6A14A]/60'}`}
                data-testid={`referral-chip-${value}`}>
                {label}
              </button>
            ))}
          </div>
          {form.referralSource === 'other' && (
            <Input autoFocus placeholder="Tell us where you found us" value={form.referralOther} onChange={set('referralOther')} className={inputCls} data-testid="register-referral-other-input" />
          )}
        </>
      ),
    },
  };

  const key = STEPS[index];
  const screen = screens[key];
  const isLast = index === STEPS.length - 1;
  const next = () => (isLast ? submit() : go(1));
  const skip = () => {
    if (key === 'company') setForm((f) => ({ ...f, company: '' }));
    if (key === 'phone') setForm((f) => ({ ...f, phone: '' }));
    if (key === 'referral') setForm((f) => ({ ...f, referralSource: '', referralOther: '' }));
    next();
  };

  return (
    <StepShell
      step={index + 1} total={STEPS.length} direction={direction}
      title={screen.title} hint={screen.hint} error={error}
      onBack={index > 0 ? () => go(-1) : null} onSubmit={next}
      onSkip={screen.optional ? skip : null}
      canContinue={screen.valid} loading={loading}
      continueLabel={screen.continueLabel}
      footer={screen.footer || (error.includes('already exists') ? (
        <Link to={loginLink} className="text-[#C6A14A] font-medium" data-testid="signup-existing-signin-link">Sign in to your account</Link>
      ) : null)}
    >
      {screen.body}
    </StepShell>
  );
}
